/**
 * GCS direct-upload service for the admin CMS.
 *
 * Flow:
 *   1. POST /api/admin/upload-url  → receive { uploadUrl, objectPath }
 *   2. PUT file directly to GCS using the signed URL
 *   3. POST /api/admin/upload-complete → receive { url, id }
 *
 * The backend never touches the file bytes; it only authenticates, signs,
 * validates, and persists metadata.
 */

const API_BASE = `${process.env.REACT_APP_API_URL}/api/admin`

export interface UploadURLResponse {
    uploadUrl: string
    objectPath: string
    expiresIn: number
}

export interface UploadResult {
    url: string
    id: string
}

export type GCSFileType = 'image' | 'pdf'

// ─── Step 1: request a signed URL ────────────────────────────────────────────

async function requestSignedUrl(
    contentType: string,
    fileType: GCSFileType,
    token: string
): Promise<UploadURLResponse> {
    const res = await fetch(`${API_BASE}/upload-url`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ contentType, fileType }),
    })

    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(body.error || `Failed to get upload URL (${res.status})`)
    }

    return res.json()
}

// ─── Step 2: PUT directly to GCS ─────────────────────────────────────────────

export function uploadToGCS(
    file: File,
    uploadUrl: string,
    onProgress?: (percent: number) => void
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', uploadUrl, true)
        // Content-Type must match what was signed for.
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.timeout = 10 * 60 * 1000 // 10-minute hard limit

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    onProgress(Math.round((e.loaded / e.total) * 100))
                }
            }
        }

        xhr.onload = () => {
            // GCS returns 200 for simple PUT uploads.
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
            } else {
                reject(
                    new Error(
                        `GCS upload failed: ${xhr.status} ${xhr.statusText}`
                    )
                )
            }
        }

        xhr.onerror = () =>
            reject(new Error('Network error during GCS upload'))
        xhr.ontimeout = () =>
            reject(new Error('Upload timed out after 10 minutes'))

        xhr.send(file)
    })
}

// ─── Step 3: notify backend ───────────────────────────────────────────────────

async function completeUpload(
    objectPath: string,
    fileName: string,
    contentType: string,
    size: number,
    fileType: GCSFileType,
    token: string
): Promise<UploadResult> {
    const res = await fetch(`${API_BASE}/upload-complete`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            objectPath,
            fileName,
            contentType,
            size,
            fileType,
        }),
    })

    if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error(body.error || `Upload completion failed (${res.status})`)
    }

    return res.json()
}

// ─── Convenience function used by all upload components ──────────────────────

/**
 * Executes the full 3-step signed-URL upload flow.
 *
 * @param file       The File object to upload.
 * @param fileType   'image' for PNG/JPG banners/covers; 'pdf' for product PDFs.
 * @param onProgress Optional progress callback (0–100).
 */
export async function uploadFileToGCS(
    file: File,
    fileType: GCSFileType,
    onProgress?: (percent: number) => void
): Promise<UploadResult> {
    const token = window.localStorage.getItem('access_token') ?? ''

    // Step 1
    const { uploadUrl, objectPath } = await requestSignedUrl(
        file.type,
        fileType,
        token
    )

    // Step 2
    await uploadToGCS(file, uploadUrl, onProgress)

    // Step 3
    return completeUpload(
        objectPath,
        file.name,
        file.type,
        file.size,
        fileType,
        token
    )
}
